import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

export const DeleteIdeaGroup = graphql(/* GraphQL */ `
  mutation DeleteIdeaGroup($id: ID!) {
    deleteIdeaGroup(input: { id: $id }) {
      __typename
      ... on MutationError {
        message
      }
      ... on DeleteIdeaGroupResponse {
        updatedIdeas {
          id
          groupId
          position
        }
      }
    }
  }
`)

const useDeleteIdeaGroup = (): ((id: string) => Promise<void>) => {
  const [deleteIdeaGroupMutation] = useMutation(DeleteIdeaGroup, {
    update: (cache, { data }, { variables }) => {
      if (variables?.id) {
        const normalizedId = cache.identify({
          id: variables.id,
          __typename: 'IdeaGroup',
        })
        cache.evict({ id: normalizedId })
        cache.gc()
      }
      if (data?.deleteIdeaGroup.__typename === 'DeleteIdeaGroupResponse') {
        data.deleteIdeaGroup.updatedIdeas.forEach((idea) => {
          cache.modify({
            id: cache.identify(idea),
            fields: {
              groupId: () => idea.groupId ?? null,
              position: () => idea.position ?? null,
            },
          })
        })
      }
    },
  })

  return useCallback(
    async (id: string) => {
      try {
        const { data } = await deleteIdeaGroupMutation({
          variables: { id },
        })

        if (isError(data?.deleteIdeaGroup)) {
          throw new Error(
            data?.deleteIdeaGroup.message ?? 'Unexpected API response',
          )
        }
      } catch (error) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          metaData: { variables: { id } },
        })
        throw error
      }
    },
    [deleteIdeaGroupMutation],
  )
}

const isError = (
  response:
    | ResultOf<typeof DeleteIdeaGroup>['deleteIdeaGroup']
    | null
    | undefined,
): response is Exclude<
  ResultOf<typeof DeleteIdeaGroup>['deleteIdeaGroup'],
  { __typename?: 'DeleteIdeaGroupResponse' }
> => {
  return !response || response?.__typename !== 'DeleteIdeaGroupResponse'
}

export default useDeleteIdeaGroup
